import { useLayoutEffect, useRef, useState } from "react";
import {
  countSignificant,
  indexAfterSignificant,
  parseSaudiPhone,
} from "../saudiPhone";
import FloatingField from "./FloatingField";

interface ISaudiPhoneFieldProps {
  /** National digits, as the form stores them. */
  value: string;
  onChange: (national: string) => void;
  onBlur: () => void;
  label: string;
  errorMessage?: string;
}

/**
 * The buyer's mobile: accepts "+966 5…", "00966 5…", "05…" or "5…", typed or pasted, and groups
 * the digits as they are typed without moving the caret.
 */
const SaudiPhoneField = ({
  value,
  onChange,
  onBlur,
  label,
  errorMessage,
}: ISaudiPhoneFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [display, setDisplay] = useState(() => parseSaudiPhone(value).display);
  const caret = useRef<number | null>(null);
  // The national digits this field last reported, to tell its own updates from outside ones.
  const emitted = useRef(value);

  // A value set from outside (a resumed draft, a form reset) replaces what is shown.
  if (value !== emitted.current) {
    emitted.current = value;
    if (parseSaudiPhone(display).national !== value)
      setDisplay(parseSaudiPhone(value).display);
  }

  useLayoutEffect(() => {
    if (caret.current === null || !inputRef.current) return;
    inputRef.current.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  const apply = (text: string, caretCount: number) => {
    const parsed = parseSaudiPhone(text);
    // "966…" gains a "+" and "00966…" trades "00" for one: shift the caret by the difference.
    const typedPlus = /^\s*\+/.test(text);
    const typedZeros = !typedPlus && /^\s*0\s*0/.test(text);
    const shownPlus = parsed.display.startsWith("+");
    const shift = shownPlus ? (typedPlus ? 0 : typedZeros ? -1 : 1) : 0;
    caret.current = indexAfterSignificant(
      parsed.display,
      Math.max(0, caretCount + shift)
    );
    setDisplay(parsed.display);
    if (parsed.national !== value) {
      emitted.current = parsed.national;
      onChange(parsed.national);
    }
  };

  return (
    <FloatingField
      ref={inputRef}
      ltr
      label={label}
      value={display}
      inputMode="tel"
      autoComplete="tel-national"
      errorMessage={errorMessage}
      onBlur={onBlur}
      onKeyDown={(event) => {
        // Backspace right after a space removes the digit before it, instead of the space
        // coming straight back.
        const input = event.currentTarget;
        const at = input.selectionStart ?? 0;
        if (
          event.key === "Backspace" &&
          at === input.selectionEnd &&
          at > 0 &&
          display[at - 1] === " "
        ) {
          event.preventDefault();
          const text = display.slice(0, at - 2) + display.slice(at);
          apply(text, countSignificant(display, at - 2));
        }
      }}
      onChange={(event) => {
        const { value: text, selectionStart } = event.target;
        apply(text, countSignificant(text, selectionStart ?? text.length));
      }}
    />
  );
};

export default SaudiPhoneField;
